import { t as uiText } from '../i18n';
import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { auth } from '../lib/firebase';
import { User, onAuthStateChanged, signInWithCustomToken } from 'firebase/auth';
import { useQueryClient } from '@tanstack/react-query';
import { errorTracking } from '../utils/errorTracking';
import { authRealtimeMonitor } from '../features/auth/AuthRealtimeMonitor';
import { authCacheManager } from '../features/auth/AuthCacheManager';
import { getUserImageURL } from '../features/auth/userImageUtils';
import { authQueryKeys, useAuthQuery } from '../features/auth/queries/authQueries';
import { useEmbedMode } from '../hooks/useEmbedMode';

interface UserImageData {
  url: string | null;
  data: string | null; // base64形式の画像データ
  loading: boolean;
  error: string | null;
}

interface AuthContextType {
  user: User | null;
  initializing: boolean; // 初回 onAuthStateChanged を受けるまで true
  stabilizing: boolean; // 直前までログインしていたのに user=null が一瞬出た時の保護
  userImage: UserImageData;
  loadUserImage: (userId?: string) => Promise<void>;
  updateUserImage: (imageData: string) => Promise<void>;
  // 新規追加: 登録プロセス制御
  isUserRegistrationInProgress: boolean;
  setIsUserRegistrationInProgress: (value: boolean) => void;
  // 埋め込みモード判定
  isEmbedMode: boolean;
}

const AuthContext = createContext<AuthContextType>({ 
  user: null, 
  initializing: true,
  stabilizing: false,
  userImage: { url: null, data: null, loading: false, error: null },
  loadUserImage: async () => {},
  updateUserImage: async () => {},
  // 新規追加: 登録プロセス制御のデフォルト値
  isUserRegistrationInProgress: false,
  setIsUserRegistrationInProgress: () => {},
  // 埋め込みモードのデフォルト値
  isEmbedMode: false
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const { isEmbedMode, authToken } = useEmbedMode();
  const { data: user } = useAuthQuery();
  const [initializing, setInitializing] = useState<boolean>(true);
  const [stabilizing, setStabilizing] = useState<boolean>(false);
  const [userImage, setUserImage] = useState<UserImageData>({
    url: null,
    data: null,
    loading: false,
    error: null
  });
  const [isUserRegistrationInProgress, setIsUserRegistrationInProgress] = useState(false);
  const previousAuthedUserIdRef = React.useRef<string | null>(null);
  const pendingLogoutTimerRef = React.useRef<number | null>(null);

  // 画像データをURLから読み込む関数（useCallbackで最適化）
  const loadUserImage = useCallback(async (userId?: string) => {
    const targetUid = userId ?? user?.uid;

    if (!targetUid) {
      setUserImage({ url: null, data: null, loading: false, error: null });
      return;
    }

    setUserImage(prev => ({ ...prev, loading: true, error: null }));

    try {
      const imageURL = await getUserImageURL({ uid: targetUid } as User);

      if (imageURL) {
        setUserImage({
          url: imageURL,
          data: null,
          loading: false,
          error: null
        });
      } else {
        setUserImage({
          url: null,
          data: null,
          loading: false,
          error: null
        });
      }
    } catch (error) {
      console.error('❌ [AuthContext] 画像読み込みエラー:', error);
      setUserImage({
        url: null,
        data: null,
        loading: false,
        error: error instanceof Error ? error.message : uiText("Failed to load the image")
      });
    }
  }, [user]);

  // 画像データを更新する関数（useCallbackで最適化）
  const updateUserImage = useCallback(async (imageData: string) => {
    setUserImage(prev => ({ ...prev, loading: true, error: null }));

    try {
      // ここでFirebase Storageに画像をアップロードする処理を追加できます
      // 現在はローカル状態のみ更新
      setUserImage({
        url: imageData, // 一時的にbase64データをURLとして使用
        data: imageData,
        loading: false,
        error: null
      });
    } catch (error) {
      setUserImage(prev => ({ 
        ...prev, 
        loading: false, 
        error: error instanceof Error ? error.message : uiText("Failed to update the image") 
      }));
    }
  }, []);

  const initializeAuthCache = useCallback((userId: string, currentWorkspaceId?: string) => {
    try {
      // リアルタイム監視を開始
      authRealtimeMonitor.initializeSubscriptions(userId, currentWorkspaceId);
    } catch (error) {
      console.error('❌ [AuthContext] 認証キャッシュ初期化エラー:', error);
    }
  }, []);

  useEffect(() => {
    if (!auth) {
      console.warn('🔐 [AuthProvider] Auth is not initialized. Skipping onAuthStateChanged.');
      setInitializing(false);
      return;
    }

    // 埋め込みモードでCustomTokenがある場合は認証を実行
    if (isEmbedMode && authToken) {
      const embedAuthStartTime = performance.now();

      signInWithCustomToken(auth, authToken)
        .then(() => {
        const embedAuthEndTime = performance.now();
      })
        .catch((error) => {
          console.error('❌ [AuthProvider] 埋め込みモード: CustomToken認証失敗', error);
          setInitializing(false);
        });
    }

    // Firebase Auth の状態変更を監視
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        // 以前の「揺れ」保護を解除
        if (pendingLogoutTimerRef.current) {
          window.clearTimeout(pendingLogoutTimerRef.current);
          pendingLogoutTimerRef.current = null;
        }
        setStabilizing(false);
        const previousUid = previousAuthedUserIdRef.current;
        if (previousUid && previousUid !== firebaseUser.uid) {
          // ログアウトを挟まず uid が入れ替わると、前の users/{uid}/workspaces 購読が残る。
          authRealtimeMonitor.cleanup();
        }
        previousAuthedUserIdRef.current = firebaseUser.uid;

        // user クエリを書き換えて購読者に反映
        queryClient.setQueryData(authQueryKeys.user(), firebaseUser);
        setInitializing(false);
        
        // Crashlyticsにユーザー情報を設定
        const userInfo: Record<string, string | undefined> = {};
        if (firebaseUser.email) userInfo.email = firebaseUser.email;
        if (firebaseUser.displayName) userInfo.display_name = firebaseUser.displayName;
        if (firebaseUser.providerData[0]?.providerId) userInfo.provider = firebaseUser.providerData[0].providerId;

        errorTracking.setUser(firebaseUser.uid, userInfo);

        // 認証キャッシュを初期化
        initializeAuthCache(firebaseUser.uid);

        // ログイン時：ユーザー依存クエリを再評価
        queryClient.invalidateQueries({ queryKey: authQueryKeys.all, exact: false });
      } else {
        // 直前までログインしていたのに一瞬 user=null が来るケースがあり、
        // その瞬間に permission-denied → access-denied になってしまうことがあるため、
        // 少しだけ猶予を持って「確定ログアウト」かどうか判定する。
        const hadPreviousUser = !!previousAuthedUserIdRef.current;

        // 購読を未認証のまま残すと permission-denied になる。
        // 2秒の猶予は画面とキャッシュの確定ログアウト判定だけに使う。
        authRealtimeMonitor.cleanup();

        // user クエリは null にして新規クエリ発火を止める（AccessControl 側で stabilizing を見て待機）
        queryClient.setQueryData(authQueryKeys.user(), null);
        setInitializing(false);

        if (!isEmbedMode && hadPreviousUser) {
          setStabilizing(true);

          if (pendingLogoutTimerRef.current) {
            window.clearTimeout(pendingLogoutTimerRef.current);
          }

          pendingLogoutTimerRef.current = window.setTimeout(() => {
            // まだ null なら確定ログアウトとして扱う
            if (!auth.currentUser) {
              console.log('⚠️ [AuthProvider] ユーザーログアウト（確定）', {
                isEmbedMode,
                timestamp: new Date().toISOString()
              });

              setStabilizing(false);
              previousAuthedUserIdRef.current = null;

              // ログアウト時：ユーザー依存のキャッシュを掃除
              // 順序が重要：まずremoveQueries → その後invalidateQueries
              const removeStartTime = performance.now();
              queryClient.removeQueries({
                predicate: (q) => {
                  const k = q.queryKey as unknown[];
                  return Array.isArray(k) &&
                    k.length >= 2 &&
                    k[0] === 'auth' &&
                    (k[1] === 'workspaceAccess' || k[1] === 'userWorkspaces');
                },
              });
              const removeEndTime = performance.now();

              // 削除後に他の auth 配下も再評価させる
              queryClient.invalidateQueries({ queryKey: authQueryKeys.all, exact: false });
              const invalidateEndTime = performance.now();

              console.log('🧹 [AuthProvider] キャッシュクリア完了', {
                removeQueriesDuration: removeEndTime - removeStartTime,
                invalidateDuration: invalidateEndTime - removeEndTime,
                timestamp: new Date().toISOString()
              });

              // 既存のキャッシュもクリア（React Queryクライアントを渡す）
              authCacheManager.clearCache(queryClient);
              authRealtimeMonitor.cleanup();
            } else {
              // auth が復帰していれば何もしない（次の onAuthStateChanged で user が入る）
              setStabilizing(false);
            }
            pendingLogoutTimerRef.current = null;
          }, 2000);
          return;
        }

        // 初回未ログイン/埋め込みモードなどは即時ログアウトとして扱う
        console.log('⚠️ [AuthProvider] ユーザーログアウト', {
          isEmbedMode,
          timestamp: new Date().toISOString()
        });

        setStabilizing(false);
        previousAuthedUserIdRef.current = null;

        // ログアウト時：ユーザー依存のキャッシュを掃除
        // 順序が重要：まずremoveQueries → その後invalidateQueries
        const removeStartTime = performance.now();
        queryClient.removeQueries({
          predicate: (q) => {
            const k = q.queryKey as unknown[];
            return Array.isArray(k) &&
              k.length >= 2 &&
              k[0] === 'auth' &&
              (k[1] === 'workspaceAccess' || k[1] === 'userWorkspaces');
          },
        });
        const removeEndTime = performance.now();

        // 削除後に他の auth 配下も再評価させる
        queryClient.invalidateQueries({ queryKey: authQueryKeys.all, exact: false });
        const invalidateEndTime = performance.now();

        console.log('🧹 [AuthProvider] キャッシュクリア完了', {
          removeQueriesDuration: removeEndTime - removeStartTime,
          invalidateDuration: invalidateEndTime - removeEndTime,
          timestamp: new Date().toISOString()
        });

        // 既存のキャッシュもクリア（React Queryクライアントを渡す）
        authCacheManager.clearCache(queryClient);
        authRealtimeMonitor.cleanup();
      }
    }, (error) => {
      console.error('🔐 [AuthProvider] 認証状態の監視でエラーが発生:', {
        error,
        errorCode: (error as any)?.code,
        errorMessage: (error as any)?.message,
        timestamp: new Date().toISOString()
      });
      setStabilizing(false);
      queryClient.setQueryData(authQueryKeys.user(), null);
      setInitializing(false);
    });

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
      if (pendingLogoutTimerRef.current) {
        window.clearTimeout(pendingLogoutTimerRef.current);
        pendingLogoutTimerRef.current = null;
      }
      // コンポーネントアンマウント時にクリーンアップ
      authRealtimeMonitor.cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, isEmbedMode, authToken]);

  // ユーザーが変更されたときに画像を読み込む
  useEffect(() => {
    if (!user) {
      setUserImage({ 
        url: null, 
        data: null, 
        loading: false, 
        error: null 
      });
      return;
    }

    if (isUserRegistrationInProgress) {
      return;
    }

    // Google認証の場合は、まずphotoURLを直接チェック
    if (user.photoURL && (
      user.photoURL.includes('googleusercontent.com') || 
      user.photoURL.includes('googleapis.com') ||
      user.photoURL.includes('appleid.apple.com') ||
      user.photoURL.includes('gravatar.com')
    )) {
      setUserImage({
        url: user.photoURL,
        data: null,
        loading: false,
        error: null
      });
      return;
    }

    // その他の場合は通常の画像読み込み処理
    loadUserImage(user.uid);
  }, [user, loadUserImage, isUserRegistrationInProgress]);

  // Provider valueをuseMemoで最適化
  const contextValue = useMemo<AuthContextType>(() => ({
    user: user ?? null, 
    initializing, 
    stabilizing,
    userImage, 
    loadUserImage, 
    updateUserImage,
    // 新規追加: 登録プロセス制御
    isUserRegistrationInProgress,
    setIsUserRegistrationInProgress,
    // 埋め込みモード判定
    isEmbedMode
  }), [user, initializing, stabilizing, userImage, loadUserImage, updateUserImage, isUserRegistrationInProgress, setIsUserRegistrationInProgress, isEmbedMode]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  return useContext(AuthContext);
};

// 後方互換性のため既存の名前も残す（段階的移行用）
export const useAuthContext = useAuth;
